import React, { ReactNode, useState } from "react";

import { Drawer, Icon, Segmented, Typography } from "components";

export type EvidenceTabKey = "docScan" | "remoteSensing";

export type EvidenceTab = {
  key: EvidenceTabKey;
  label: string;
  iconName: string;
  content: ReactNode;
};

type EvidenceDrawerProps = {
  title: string;
  tabs: EvidenceTab[];
  defaultTabKey: EvidenceTabKey;
  isOpen: boolean;
  onClose: () => void;
};

// Mounted only while the drawer is open, so each opening starts on the tab of the evidence that was clicked.
const EvidenceTabs = ({ tabs, defaultTabKey }: Pick<EvidenceDrawerProps, "tabs" | "defaultTabKey">) => {
  const [activeTabKey, setActiveTabKey] = useState(defaultTabKey);
  const activeTab = tabs.find(({ key }) => key === activeTabKey) ?? tabs[0];

  return (
    <>
      <Segmented<EvidenceTabKey>
        block
        className="evidence-drawer-tabs"
        value={activeTab?.key}
        onChange={setActiveTabKey}
        options={tabs.map(({ key, label, iconName }) => ({
          value: key,
          label: (
            <span className="evidence-drawer-tab">
              <Icon iconName={iconName} size="s" />
              {label}
            </span>
          ),
        }))}
      />
      <div className="evidence-drawer-content">{activeTab?.content}</div>
    </>
  );
};

/** Drawer for the automated evidence of one field and year, with one tab per evidence source. */
export const EvidenceDrawer = ({ title, tabs, defaultTabKey, isOpen, onClose }: EvidenceDrawerProps) => (
  <Drawer visible={isOpen} onClose={onClose} title={title} className="evidence-drawer" dataTestId="evidence-drawer">
    <Typography variant="paragraph-m" color="grey-600" className="evidence-drawer-subtitle">
      Automated evidence — it never changes verification state. You still decide.
    </Typography>
    <EvidenceTabs tabs={tabs} defaultTabKey={defaultTabKey} />
  </Drawer>
);
